import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { boardPointLabel } from '../domain/game-interaction';
import {
  boardPointWithinFocus,
  boardViewportFocusBounds,
  nextVisibleBoardFocus,
  visibleBoardFocus,
} from '../domain/board-keyboard-focus';
import type {
  BoardPoint,
  BoardSize,
  StoneColor,
  ProblemBoardAnnotation,
} from '@wx-weiqi/contracts';
import './teacher-board.css';
import { ProblemAnnotationLayer } from './ProblemAnnotationLayer';

import {
  BOARD_VIEW_SIZE,
  boardCoordinate,
  boardPadding,
  pointKey,
  starPoints,
  stoneRadius,
  type BoardViewport,
} from '../domain/board-geometry';
import {
  aiCandidateAppearance,
  aiCandidateMarkerLayout,
  aiCandidateValueFontSize,
  formatAiScoreLead,
  type AiCandidatePresentation,
} from '../domain/ai-candidate-presentation';

interface BoardSurfaceProps {
  readonly keyboardNavigation?: boolean;
  readonly previewPoint?: BoardPoint | null;
  readonly emphasizeLastMove?: boolean;
  readonly boardAppearance?: 'classic' | 'wood';
  readonly stoneAppearance?: 'classic' | 'shell';
  readonly size: BoardSize;
  readonly stones: readonly (StoneColor | null)[];
  readonly blackTerritory?: readonly BoardPoint[];
  readonly whiteTerritory?: readonly BoardPoint[];
  readonly deadStoneKeys?: ReadonlySet<string>;
  readonly selectedStoneKeys?: ReadonlySet<string>;
  readonly lastMove: BoardPoint | null;
  readonly initialAnnotations?: readonly ProblemBoardAnnotation[];
  readonly stoneLabels?: readonly {
    readonly point: BoardPoint;
    readonly label: string;
    readonly color: StoneColor;
  }[];
  readonly aiCandidates?: readonly AiCandidatePresentation[];
  readonly pointMarkers?: readonly {
    readonly point: BoardPoint;
    readonly color: string;
  }[];
  readonly choiceMarkers?: readonly {
    readonly point: BoardPoint;
    readonly color: string;
    readonly label: string;
  }[];
  readonly isActionable?: (point: BoardPoint, stone: StoneColor | null) => boolean;
  readonly onPoint?: (point: BoardPoint) => void;
  readonly onPointContextMenu?: (point: BoardPoint, event: MouseEvent<SVGCircleElement>) => void;
  readonly annotationLayer?: ReactNode;
  // Visual overlays use the same SVG coordinates and never replace hit targets.
  readonly svgOverlay?: ReactNode;
  readonly viewport?: BoardViewport | null;
  readonly coordinateEdges?: Readonly<{
    readonly top?: boolean;
    readonly right?: boolean;
    readonly bottom?: boolean;
    readonly left?: boolean;
  }>;
  readonly onContextMenu?: (event: MouseEvent<HTMLDivElement>) => void;
}

const EMPTY_KEYS = new Set<string>();
// Alpha-bounds measurements from the source sprites, in pixels. Keep their visible centers
// on the shared board intersection and both visible diameters equal to the native stones.
const SHELL_SPRITES = {
  BLACK: { width: 1034, height: 1026, cx: 626.5, cy: 626.5 },
  WHITE: { width: 1038, height: 1028, cx: 626.5, cy: 622.5 },
} as const;
const BOARD_COORDINATE_LETTERS = 'ABCDEFGHJKLMNOPQRSTUVWXYZ';

export function BoardSurface({
  keyboardNavigation = false,
  previewPoint = null,
  emphasizeLastMove = false,
  boardAppearance = 'classic',
  stoneAppearance = boardAppearance === 'wood' ? 'shell' : 'classic',
  size,
  stones,
  blackTerritory = [],
  whiteTerritory = [],
  deadStoneKeys = EMPTY_KEYS,
  selectedStoneKeys = EMPTY_KEYS,
  lastMove,
  stoneLabels = [],
  initialAnnotations = [],
  aiCandidates = [],
  pointMarkers = [],
  choiceMarkers = [],
  isActionable,
  onPoint,
  onPointContextMenu,
  annotationLayer,
  svgOverlay,
  viewport = null,
  coordinateEdges = boardAppearance === 'wood'
    ? {
        top: viewport === null || viewport.y === 0,
        left: viewport === null || viewport.x === 0,
        bottom: viewport !== null && viewport.y > 0,
        right: viewport !== null && viewport.x > 0,
      }
    : undefined,
  onContextMenu,
}: BoardSurfaceProps) {
  const [focusIndex, setFocusIndex] = useState(0);
  const boardRoot = useRef<HTMLDivElement>(null);
  const focusBounds = boardViewportFocusBounds(size, viewport);
  const activeFocusIndex = visibleBoardFocus(focusIndex, size, focusBounds);
  useEffect(() => {
    if (!keyboardNavigation || activeFocusIndex === null) return;
    if (focusIndex !== activeFocusIndex) setFocusIndex(activeFocusIndex);
    const focused = document.activeElement;
    // A zoom change may hide the currently focused intersection. Move that
    // focus within the board, but never steal it from the zoom/menu controls.
    if (
      focused?.hasAttribute('data-board-index') &&
      boardRoot.current?.contains(focused) &&
      focused.getAttribute('data-board-index') !== String(activeFocusIndex)
    ) {
      boardRoot.current
        .querySelector<SVGCircleElement>(`[data-board-index="${activeFocusIndex}"]`)
        ?.focus();
    }
  }, [keyboardNavigation, activeFocusIndex, focusIndex]);
  const padding = boardPadding(size);
  const lineEnd = BOARD_VIEW_SIZE - padding;
  const radius = stoneRadius(size);
  const candidateMarkerLayout = aiCandidateMarkerLayout(radius);
  const candidatesByPoint = new Map(
    aiCandidates.map((candidate) => [pointKey(candidate.point), candidate] as const),
  );
  const pointMarkerGroups = new Map<string, typeof pointMarkers>();
  pointMarkers.forEach((marker) => {
    const key = pointKey(marker.point);
    pointMarkerGroups.set(key, [...(pointMarkerGroups.get(key) ?? []), marker]);
  });
  const surface = (
    <>
      <svg
        className="go-board"
        viewBox={
          viewport === null
            ? `0 0 ${BOARD_VIEW_SIZE} ${BOARD_VIEW_SIZE}`
            : `${viewport.x} ${viewport.y} ${viewport.width} ${viewport.height}`
        }
        role="grid"
      >
        <defs>
          <radialGradient id="black-stone" cx="35%" cy="28%" r="72%">
            <stop offset="0%" stopColor="#5c6462" />
            <stop offset="48%" stopColor="#202625" />
            <stop offset="100%" stopColor="#080a0a" />
          </radialGradient>
          <radialGradient id="white-stone" cx="35%" cy="28%" r="72%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="70%" stopColor="#ebe9df" />
            <stop offset="100%" stopColor="#c8c4b8" />
          </radialGradient>
          <filter id="stone-shadow" x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="5" dy="7" stdDeviation="5" floodOpacity="0.32" />
          </filter>
        </defs>

        {Array.from({ length: size }, (_, index) => {
          const coordinate = boardCoordinate(size, index);
          return (
            <g key={`line-${index}`} className="board-lines">
              <line x1={padding} y1={coordinate} x2={lineEnd} y2={coordinate} />
              <line x1={coordinate} y1={padding} x2={coordinate} y2={lineEnd} />
            </g>
          );
        })}

        {coordinateEdges === undefined ? null : (
          <g className="board-coordinates" aria-hidden="true">
            {Array.from({ length: size }, (_, index) => {
              const coordinate = boardCoordinate(size, index);
              const letter = BOARD_COORDINATE_LETTERS[index] ?? String(index + 1);
              const number = String(size - index);
              return (
                <g key={`coordinate-${index}`}>
                  {coordinateEdges.top === true ? (
                    <text
                      className="board-coordinate board-coordinate--top"
                      x={coordinate}
                      y="11"
                      textAnchor="middle"
                      dominantBaseline="central"
                    >
                      {letter}
                    </text>
                  ) : null}
                  {coordinateEdges.bottom === true ? (
                    <text
                      className="board-coordinate board-coordinate--bottom"
                      x={coordinate}
                      y={BOARD_VIEW_SIZE - 11}
                      textAnchor="middle"
                      dominantBaseline="central"
                    >
                      {letter}
                    </text>
                  ) : null}
                  {coordinateEdges.left === true ? (
                    <text
                      className="board-coordinate board-coordinate--left"
                      x="10"
                      y={coordinate}
                      textAnchor="middle"
                      dominantBaseline="central"
                    >
                      {number}
                    </text>
                  ) : null}
                  {coordinateEdges.right === true ? (
                    <text
                      className="board-coordinate board-coordinate--right"
                      x={BOARD_VIEW_SIZE - 10}
                      y={coordinate}
                      textAnchor="middle"
                      dominantBaseline="central"
                    >
                      {number}
                    </text>
                  ) : null}
                </g>
              );
            })}
          </g>
        )}

        {starPoints(size).map((point) => (
          <circle
            key={`star-${pointKey(point)}`}
            cx={boardCoordinate(size, point.x)}
            cy={boardCoordinate(size, point.y)}
            r={size === 19 ? 8 : 10}
            className="star-point"
          />
        ))}

        {blackTerritory.map((point) => (
          <circle
            key={`black-territory-${pointKey(point)}`}
            cx={boardCoordinate(size, point.x)}
            cy={boardCoordinate(size, point.y)}
            r={radius * 0.14}
            className="territory-mark territory-mark--black"
            aria-hidden="true"
          />
        ))}

        {whiteTerritory.map((point) => (
          <circle
            key={`white-territory-${pointKey(point)}`}
            cx={boardCoordinate(size, point.x)}
            cy={boardCoordinate(size, point.y)}
            r={radius * 0.14}
            className="territory-mark territory-mark--white"
            aria-hidden="true"
          />
        ))}

        {stones.map((stone, index) => {
          if (stone === null) return null;
          const point = { x: index % size, y: Math.floor(index / size) };
          const key = pointKey(point);
          const sprite = SHELL_SPRITES[stone];
          return (
            <g key={`stone-${key}`} filter="url(#stone-shadow)">
              {stoneAppearance === 'shell' ? (
                <image
                  className="board-shell-stone"
                  href={`/assets/teacher-lecture/${stone === 'BLACK' ? 'black-slate' : 'white-shell'}.png`}
                  x={boardCoordinate(size, point.x) - (sprite.cx / sprite.width) * radius * 2}
                  y={boardCoordinate(size, point.y) - (sprite.cy / sprite.height) * radius * 2}
                  width={(1254 / sprite.width) * radius * 2}
                  height={(1254 / sprite.height) * radius * 2}
                  preserveAspectRatio="none"
                  aria-hidden="true"
                />
              ) : (
                <circle
                  cx={boardCoordinate(size, point.x)}
                  cy={boardCoordinate(size, point.y)}
                  r={radius}
                  fill={stone === 'BLACK' ? 'url(#black-stone)' : 'url(#white-stone)'}
                  stroke={stone === 'BLACK' ? '#060807' : '#aaa69b'}
                  strokeWidth="2"
                />
              )}
              {deadStoneKeys.has(key) ? (
                <path
                  d={`M ${boardCoordinate(size, point.x) - radius / 2} ${boardCoordinate(size, point.y) - radius / 2} L ${boardCoordinate(size, point.x) + radius / 2} ${boardCoordinate(size, point.y) + radius / 2} M ${boardCoordinate(size, point.x) + radius / 2} ${boardCoordinate(size, point.y) - radius / 2} L ${boardCoordinate(size, point.x) - radius / 2} ${boardCoordinate(size, point.y) + radius / 2}`}
                  className="dead-mark"
                />
              ) : null}
              {selectedStoneKeys.has(key) ? (
                <circle
                  cx={boardCoordinate(size, point.x)}
                  cy={boardCoordinate(size, point.y)}
                  r={radius * 0.48}
                  className="scoring-dispute-mark"
                />
              ) : null}
            </g>
          );
        })}

        {previewPoint !== null ? (
          <circle
            cx={boardCoordinate(size, previewPoint.x)}
            cy={boardCoordinate(size, previewPoint.y)}
            r={radius * 0.85}
            className="board-move-preview"
            aria-hidden="true"
          />
        ) : null}
        {lastMove !== null ? (
          <circle
            cx={boardCoordinate(size, lastMove.x)}
            cy={boardCoordinate(size, lastMove.y)}
            r={radius * (emphasizeLastMove ? 0.44 : 0.22)}
            className={`last-move-mark${emphasizeLastMove ? ' last-move-mark--emphasized' : ''}`}
            vectorEffect={emphasizeLastMove ? 'non-scaling-stroke' : undefined}
            aria-hidden="true"
          />
        ) : null}

        {[...pointMarkerGroups.entries()].flatMap(([key, markers]) =>
          markers.map((marker, index) => {
            const angle = markers.length === 1 ? 0 : (Math.PI * 2 * index) / markers.length;
            const offset = markers.length === 1 ? 0 : radius * 0.28;
            return (
              <circle
                key={`point-marker-${key}-${index}`}
                cx={boardCoordinate(size, marker.point.x) + Math.cos(angle) * offset}
                cy={boardCoordinate(size, marker.point.y) + Math.sin(angle) * offset}
                r={radius * (markers.length === 1 ? 0.22 : 0.14)}
                fill={marker.color}
                className="board-point-marker"
                aria-hidden="true"
              />
            );
          }),
        )}

        {choiceMarkers.map((marker) => (
          <g
            key={`choice-marker-${pointKey(marker.point)}`}
            className="board-choice-marker"
            aria-hidden="true"
          >
            <circle
              cx={boardCoordinate(size, marker.point.x)}
              cy={boardCoordinate(size, marker.point.y)}
              r={radius * 0.72}
              fill={marker.color}
            />
            <text
              x={boardCoordinate(size, marker.point.x)}
              y={boardCoordinate(size, marker.point.y)}
              fontSize={radius * (marker.label.length > 2 ? 0.58 : 0.8)}
              textAnchor="middle"
              dominantBaseline="central"
            >
              {marker.label}
            </text>
          </g>
        ))}

        {stoneLabels.map((marker) => (
          <text
            key={`stone-label-${pointKey(marker.point)}-${marker.label}`}
            x={boardCoordinate(size, marker.point.x)}
            y={boardCoordinate(size, marker.point.y)}
            className={`stone-move-label stone-move-label--${marker.color.toLowerCase()}`}
            style={
              size === 19
                ? undefined
                : {
                    fontSize: `${radius * Math.min(1.05, 2.4 / Math.max(1, marker.label.length))}px`,
                  }
            }
            textAnchor="middle"
            dominantBaseline="central"
          >
            {marker.label}
          </text>
        ))}

        {initialAnnotations.length > 0 ? (
          <ProblemAnnotationLayer size={size} stones={stones} annotations={initialAnnotations} />
        ) : null}

        {aiCandidates.map((candidate) => {
          const appearance = aiCandidateAppearance(candidate.rank);
          const rate = (candidate.winRateBps / 100).toFixed(1);
          return (
            <g
              key={`ai-candidate-${pointKey(candidate.point)}`}
              className={`ai-candidate ai-candidate--${candidate.color.toLowerCase()}${viewport === null ? ' ai-candidate--full-board' : ''}`}
              style={
                {
                  '--ai-candidate-fill': appearance.fill,
                  '--ai-candidate-text': appearance.text,
                  '--ai-candidate-stroke': appearance.stroke,
                  '--ai-candidate-rate-size': `${aiCandidateValueFontSize(candidateMarkerLayout.rateFontSize, rate, 4)}px`,
                  '--ai-candidate-rank-size': `${candidateMarkerLayout.rankFontSize}px`,
                } as CSSProperties
              }
              aria-hidden="true"
            >
              <circle
                className="ai-candidate-disc"
                cx={boardCoordinate(size, candidate.point.x)}
                cy={boardCoordinate(size, candidate.point.y)}
                r={candidateMarkerLayout.circleRadius}
              />
              <text
                x={boardCoordinate(size, candidate.point.x)}
                y={boardCoordinate(size, candidate.point.y) + candidateMarkerLayout.rateOffsetY}
                className="ai-candidate-rate"
                textAnchor="middle"
                dominantBaseline="central"
              >
                {rate}
              </text>
              <text
                x={boardCoordinate(size, candidate.point.x)}
                y={boardCoordinate(size, candidate.point.y) + candidateMarkerLayout.rankOffsetY}
                className="ai-candidate-rank"
                textAnchor="middle"
                dominantBaseline="central"
              >
                {candidate.rank}
              </text>
              <text
                x={boardCoordinate(size, candidate.point.x)}
                y={boardCoordinate(size, candidate.point.y)}
                className="ai-candidate-compact-rank"
                textAnchor="middle"
                dominantBaseline="central"
              >
                {candidate.rank}
              </text>
            </g>
          );
        })}

        {svgOverlay}

        {isActionable !== undefined && onPoint !== undefined
          ? Array.from({ length: size * size }, (_, index) => {
              const point = { x: index % size, y: Math.floor(index / size) };
              const stone = stones[index] ?? null;
              const actionable = isActionable(point, stone);
              const visible = boardPointWithinFocus(point, focusBounds);
              const candidate = candidatesByPoint.get(pointKey(point));
              const candidateLabel =
                candidate === undefined
                  ? ''
                  : `，AI推荐第${candidate.rank}点，当前行棋方胜率${(
                      candidate.winRateBps / 100
                    ).toFixed(1)}%，计算量${candidate.visits}`;
              const scoreLabel =
                candidate === undefined
                  ? ''
                  : `，目差${formatAiScoreLead(candidate.scoreLeadHalfPoints)}目`;
              return (
                <circle
                  key={`hit-${pointKey(point)}`}
                  cx={boardCoordinate(size, point.x)}
                  cy={boardCoordinate(size, point.y)}
                  r={Math.max(radius, 22)}
                  className={actionable ? 'board-hit board-hit--active' : 'board-hit'}
                  role="gridcell"
                  data-board-index={index}
                  tabIndex={
                    keyboardNavigation
                      ? index === activeFocusIndex
                        ? 0
                        : -1
                      : actionable && visible
                        ? 0
                        : -1
                  }
                  aria-disabled={keyboardNavigation ? !actionable || !visible : undefined}
                  onFocus={() => {
                    if (keyboardNavigation && visible) setFocusIndex(index);
                  }}
                  aria-label={
                    keyboardNavigation
                      ? `${boardPointLabel(point, size)}，${stone === 'BLACK' ? '黑子' : stone === 'WHITE' ? '白子' : '空点'}${lastMove?.x === point.x && lastMove.y === point.y ? '，最后一手' : ''}`
                      : `${point.x + 1}列 ${point.y + 1}行${stone === null ? '' : '，有棋子'}${candidateLabel}${scoreLabel}`
                  }
                  onClick={() => actionable && onPoint(point)}
                  onContextMenu={
                    onPointContextMenu === undefined
                      ? undefined
                      : (event) => onPointContextMenu(point, event)
                  }
                  onPointerUp={(event) => event.currentTarget.blur()}
                  onKeyDown={(event) => {
                    if (
                      keyboardNavigation &&
                      ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(
                        event.key,
                      )
                    ) {
                      event.preventDefault();
                      const next = nextVisibleBoardFocus(index, size, event.key, focusBounds);
                      if (next === null) return;
                      setFocusIndex(next);
                      event.currentTarget
                        .closest('svg')
                        ?.querySelector<SVGCircleElement>(`[data-board-index="${next}"]`)
                        ?.focus();
                    }
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      if (actionable && visible) onPoint(point);
                    }
                  }}
                />
              );
            })
          : null}
      </svg>
      {annotationLayer}
    </>
  );
  return (
    <div
      ref={boardRoot}
      className={`board-shell${boardAppearance === 'wood' ? ' board-shell--wood' : ''}`}
      aria-label={`${size}路围棋棋盘`}
      onContextMenu={onContextMenu}
    >
      {boardAppearance === 'wood' ? <div className="board-wood-surface">{surface}</div> : surface}
    </div>
  );
}
